import React, { useState, useEffect } from 'react';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppToaster from '../../../components/AppToaster';
import { getFranchiseeSettings, updateFranchiseeSettings } from '../../../api/FranchiseeSettingsAPI';
import { handleApiError } from '../../../api/risapi';

const FranchiseeSettingsPage = () => {
    const [settings, setSettings] = useState(null);
    const [gateways, setGateways] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [errors, setErrors] = useState({});

    // Fetch data on load
    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        getFranchiseeSettings((response) => {
            if (isMounted) {
                if (response.settings) {
                    setSettings(response.settings);
                    if (response.payment_gateways) {
                        setGateways(response.payment_gateways);
                    }
                } else {
                    handleApiError(response);
                }
                setLoading(false);
            }
        });
        return () => { isMounted = false; };
    }, []);

    // Handle toggle switch changes for basic notification settings
    const handleToggleChange = (e) => {
        const { name, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: checked
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    // Handle Changes inside the Payment Gateway list array
    const handleGatewayToggle = (index, checked) => {
        setGateways(prev => {
            const updated = [...prev];
            updated[index].is_enabled = checked;
            return updated;
        });
    };

    const handleGatewayCredentialChange = (index, field, value) => {
        setGateways(prev => {
            const updated = [...prev];
            updated[index].credentials = {
                ...updated[index].credentials,
                [field]: value
            };
            return updated;
        });
    };

    // Handle save
    const handleSave = () => {
        setSaving(true);
        setErrors({});

        const payload = {
            enable_sms_notifications: settings.enable_sms_notifications,
            enable_whatsapp_notifications: settings.enable_whatsapp_notifications,
            enable_email_notifications: settings.enable_email_notifications,
            gateways: gateways 
        };

        updateFranchiseeSettings(payload, (response) => {
            setSaving(false);
            if (response.message) {
                if(response.settings) setSettings(response.settings); // Update state with fresh DB data
                setToaster({ type: 'success', heading: 'Success', message: response.message });
                setShowToaster(true);
            } else {
                handleApiError(response, setErrors);
                setToaster({ type: 'danger', heading: 'Error', message: 'Failed to update settings.' });
                setShowToaster(true);
            }
        });
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Franchisee Settings' />
                    {loading && <div className="loader_box"><div className="loader_14"></div></div>}
                    
                    {!loading && settings && (
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">Your Information</h5>
                            </div>
                            <div className="card-body">
                                <form className="app-form" onSubmit={(e) => e.preventDefault()}>
                                    {/* --- Read-Only Section --- */}
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Franchisee Name</label>
                                            <input type="text" className="form-control-plaintext fw-bold" value={settings.name || ''} readOnly />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">PAN</label>
                                            <input type="text" className="form-control-plaintext" value={settings.pan || ''} readOnly />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">GSTIN</label>
                                            <input type="text" className="form-control-plaintext" value={settings.gstin || 'N/A'} readOnly />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">GST Registration Type</label>
                                            <input type="text" className="form-control-plaintext text-capitalize" value={settings.gst_registration_type || ''} readOnly />
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Address</label>
                                            <textarea 
                                                className="form-control-plaintext" 
                                                readOnly 
                                                rows="2" 
                                                // Mapped to use the eager loaded relations for state and district names
                                                value={`${settings.address || ''}\n${settings.district?.name || ''}, ${settings.state?.name || ''} - ${settings.pincode || ''}`} 
                                            />
                                        </div>
                                    </div>

                                    <hr className="my-4" />

                                    {/* --- Editable Notification Section --- */}
                                    <h5 className="mb-3">Notification Settings</h5>
                                    <div className="row mb-3">
                                        <div className="col-md-4 mb-3">
                                            <div className="form-check form-switch form-switch-lg">
                                                <input 
                                                    className="form-check-input" 
                                                    type="checkbox" 
                                                    role="switch" 
                                                    id="sms-toggle"
                                                    name="enable_sms_notifications"
                                                    checked={!!settings.enable_sms_notifications}
                                                    onChange={handleToggleChange}
                                                />
                                                <label className="form-check-label" htmlFor="sms-toggle">Enable SMS Notifications</label>
                                            </div>
                                            <span className="text-danger small">{errors.enable_sms_notifications}</span>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <div className="form-check form-switch form-switch-lg">
                                                <input 
                                                    className="form-check-input" 
                                                    type="checkbox" 
                                                    role="switch" 
                                                    id="whatsapp-toggle"
                                                    name="enable_whatsapp_notifications"
                                                    checked={!!settings.enable_whatsapp_notifications}
                                                    onChange={handleToggleChange}
                                                />
                                                <label className="form-check-label" htmlFor="whatsapp-toggle">Enable WhatsApp Notifications</label>
                                            </div>
                                            <span className="text-danger small">{errors.enable_whatsapp_notifications}</span>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <div className="form-check form-switch form-switch-lg">
                                                <input 
                                                    className="form-check-input" 
                                                    type="checkbox" 
                                                    role="switch" 
                                                    id="email-toggle"
                                                    name="enable_email_notifications"
                                                    checked={!!settings.enable_email_notifications}
                                                    onChange={handleToggleChange}
                                                />
                                                <label className="form-check-label" htmlFor="email-toggle">Enable Email Notifications</label>
                                            </div>
                                            <span className="text-danger small">{errors.enable_email_notifications}</span>
                                        </div>
                                    </div>

                                    <hr className="my-4" />

                                    {/* --- Payment Gateways Section --- */}
                                    <h5 className="mb-3">Online Payment Gateways</h5>
                                    <div className="row">
                                        {gateways.length === 0 ? (
                                            <div className="col-12"><p className="text-muted">No payment providers configured globally by administration.</p></div>
                                        ) : (
                                            gateways.map((gw, idx) => (
                                                <div className="col-12 mb-4" key={gw.id}>
                                                    <div className="border p-3 rounded bg-light">
                                                        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
                                                            <h6 className="mb-0 font-weight-bold text-dark">
                                                                <i className="ti ti-credit-card me-2"></i>{gw.name} Integration
                                                            </h6>
                                                            <div className="form-check form-switch form-switch-lg mb-0 mt-2 mt-sm-0">
                                                                <input 
                                                                    className="form-check-input" 
                                                                    type="checkbox" 
                                                                    role="switch" 
                                                                    id={`gw-toggle-${gw.id}`}
                                                                    checked={gw.is_enabled}
                                                                    onChange={(e) => handleGatewayToggle(idx, e.target.checked)}
                                                                />
                                                                <label className="form-check-label" htmlFor={`gw-toggle-${gw.id}`}>
                                                                    {gw.is_enabled ? 'Active' : 'Disabled'}
                                                                </label>
                                                            </div>
                                                        </div>

                                                        {/* Credentials Collapse Window */}
                                                        {gw.is_enabled && (
                                                            <div className="row fade show mt-2">
                                                                <div className="col-md-6 mb-2">
                                                                    <label className="form-label">Production Live Key *</label>
                                                                    <input 
                                                                        type="text" 
                                                                        className="form-control bg-white" 
                                                                        placeholder={`Enter your live ${gw.name} key`}
                                                                        value={gw.credentials?.prod_key || ''} 
                                                                        onChange={(e) => handleGatewayCredentialChange(idx, 'prod_key', e.target.value)}
                                                                        required
                                                                    />
                                                                    {errors[`gateways.${idx}.credentials.prod_key`] && (
                                                                        <span className="text-danger small">{errors[`gateways.${idx}.credentials.prod_key`][0]}</span>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-6 mb-2">
                                                                    <label className="form-label">Production Live Secret *</label>
                                                                    <input 
                                                                        type="password" 
                                                                        className="form-control bg-white" 
                                                                        placeholder={`Enter your live ${gw.name} secret`}
                                                                        value={gw.credentials?.prod_secret || ''} 
                                                                        onChange={(e) => handleGatewayCredentialChange(idx, 'prod_secret', e.target.value)}
                                                                        required
                                                                    />
                                                                    {errors[`gateways.${idx}.credentials.prod_secret`] && (
                                                                        <span className="text-danger small">{errors[`gateways.${idx}.credentials.prod_secret`][0]}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    {/* --- Action Button --- */}
                                    <div className="col-12 mt-4">
                                        <div className="text-end border-top pt-3">
                                            <button className="btn btn-primary px-4" type="button" onClick={handleSave} disabled={saving}>
                                                {saving ? 'Saving...' : 'Save Settings'}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
};

export default FranchiseeSettingsPage;